import { useState, useEffect } from "react";
import { isValidEmail, isValidMobile } from "@/utils";

import { useNavigate, useLocation } from "react-router-dom";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MButton } from "@/components/ui/atoms/MButtons";
import MSelectCard from "@/components/ui/atoms/MSelectCard";
import { getMemberInfo } from "@/hooks/useMembers";
import {
  IconDeviceMobile,
  IconMail,
} from "@tabler/icons-react";
import { MInputField } from "@/components/ui/atoms/MInputField";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { registerService } from "@/services/register.service";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useTranslation } from "react-i18next";
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, getAddMemberContactScreenName } from '@/constants/analytics.constants';

const CONTACT_OPTIONS = [
  { key: "mobile", label: "Add mobile number" },
  { key: "email", label: "Add e-mail" },
];

const AddMemberContact: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { t } = useTranslation();

  // Get firstName or nickname from localStorage, fallback to 'this member'
  let firstName = "";
  const saved = localStorage.getItem("add_member_form");
  if (saved) {
    const form = JSON.parse(saved);
    firstName = form.nickname || form.firstName;
  }

  const [selected, setSelected] = useState<string>(() => {
    const saved = localStorage.getItem("add_member_form");
    if (saved) {
      const form = JSON.parse(saved);
      if (form.contactOption) return form.contactOption;
    }
    const match = location.pathname.match(/\/contact\/(email|mobile)$/);
    return match ? match[1] : "";
  });

  // Sync selected with URL on popstate
  useEffect(() => {
    const match = location.pathname.match(/\/contact\/(email|mobile)$/);
    setSelected(match ? match[1] : "");
  }, [location.pathname]);

  // Reset opposite field when switching between mobile and email
  useEffect(() => {
    if (selected === "mobile") {
      setEmailValue("");
      const saved = localStorage.getItem("add_member_form");
      const form = saved ? JSON.parse(saved) : {};
      delete form.email;
      localStorage.setItem("add_member_form", JSON.stringify(form));
    } else if (selected === "email") {
      setMobileValue("");
      const saved = localStorage.getItem("add_member_form");
      const form = saved ? JSON.parse(saved) : {};
      delete form.mobile;
      delete form.dialCode;
      localStorage.setItem("add_member_form", JSON.stringify(form));
    }
  }, [selected]);

  const [dialCode, setDialCode] = useState<string>("+91");
  const [dialCodeDrawerOpen, setDialCodeDrawerOpen] = useState(false);
  const [mobileValue, setMobileValue] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    if (saved) {
      const form = JSON.parse(saved);
      return form.mobile || "";
    }
    return "";
  });
  const [emailValue, setEmailValue] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    if (saved) {
      const form = JSON.parse(saved);
      return form.email || "";
    }
    return "";
  });
  const [mobileFocused, setMobileFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [inviteToggle, setInviteToggle] = useState(true);
  const memberInfo = getMemberInfo(true);

  // Track GTM user_data_loaded event on page load
  useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: "family_add_member_add_contact",
    });
  }, [memberInfo?.id]);

  const checkIfMemberExists = async (): Promise<boolean> => {
    let exists = false;
    if (selected === "mobile") {
      exists = await checkIfMobileExists(mobileValue);
    } else if (selected === "email") {
      exists = await checkIfEmailExists(emailValue);
    }
    if (exists) {
      showToast({
        message: t(`add_member.contact_already_registered.${selected}`),
        variant: "error",
      });
      
      // Track error event
      const errorType = selected === "mobile" ? "phone_exist" : "email_exist";
      const errorMessage = selected === "mobile" 
        ? "phone already registered" 
        : "email already registered";
      
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
        screenName: getAddMemberContactScreenName(selected === "mobile" ? "phone" : "email"),
        additionalPageData: {
          error_type: errorType,
          error_message: errorMessage,
        },
      });
    }
    return exists;
  };

  const checkIfMobileExists = async (mobileValue: string): Promise<boolean> => {
    try {
      return await registerService.checkIfExists({ mobile: mobileValue });
    } catch (error) {
      console.error("Error checking mobile existence:", error);
      throw error;
    }
  };

  const checkIfEmailExists = async (emailValue: string) => {
    try {
      return await registerService.checkIfExists({ email: emailValue });
    } catch (error) {
      console.error("Error checking email existence:", error);
      throw error;
    }
  };

  return (
    <div className="profile-page-root">
      <FormHeader
        module="Family"
        memberInfo={memberInfo}
        title={
          selected === "email"
            ? "Add email"
            : selected === "mobile"
            ? "Add mobile number"
            : `Add ${firstName}'s contact details`
        }
        description={
          selected === "mobile"
            ? "Enter their mobile number so that you can invite them to join your Maatra family."
            : selected === "email"
            ? "Enter their email so that you can invite them to join your Maatra family."
            : "Add a mobile number or e-mail to keep the profile secure and connected."
        }
        learnMoreContent={
          selected === "email" || selected === "mobile" ? (
            <>
              Contact details are used for important updates and verification.
              <br />
              <br />
              They’re stored securely and never shared without consent.
            </>
          ) : (
            <>
              We use contact details to link family profiles, enable
              coordination, and offer the option to send an invite. <br />
              <br />
              No messages are sent unless you choose to invite them.
            </>
          )
        }
      />
      <MSpacer />
      <div className="flex flex-col gap-[16px]">
        {!selected &&
          CONTACT_OPTIONS.map((option) => {
            let icon = null;
            if (option.key === "mobile")
              icon = <IconDeviceMobile size={20} stroke={1.6} />;
            if (option.key === "email")
              icon = <IconMail size={20} stroke={1.6} />;
            return (
              <MSelectCard
                key={option.key}
                text={option.label}
                selected={selected === option.key}
                onClick={() => {
                  setSelected(option.key);
                  
                  // Track contact type selection
                  const contactType = option.key === 'mobile' ? 'phone' : 'email';
                  trackUserDataLoaded({
                    memberInfo,
                    screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
                    screenName: getAddMemberContactScreenName(contactType as 'phone' | 'email'),
                  });
                  
                  // Save option.key to localStorage
                  const saved = localStorage.getItem("add_member_form");
                  const form = saved ? JSON.parse(saved) : {};
                  localStorage.setItem(
                    "add_member_form",
                    JSON.stringify({ ...form, contactOption: option.key })
                  );
                  navigate(
                    `/profile/family/add-member/form/details/invite/contact/${option.key}`,
                    { replace: true }
                  );
                }}
                leftIcon={icon}
                variant="inverse"
                style={{
                  gap: "16px",
                }}
              />
            );
          })}

        {selected === "mobile" && (
          <>
            <div className="add-member-label">What’s their mobile number?</div>
            <div className="flex flex-row items-center" style={{ gap: 12 }}>
              <div className="add-member-dialcode">
                <MInputField
                  placeholder="Dial code"
                  value={dialCode}
                  readOnly
                  onChange={(e) => setDialCode(e.target.value)}
                  type="text"
                  showLabel={false}
                  style={{ minWidth: 0 }}
                />
              </div>
              <MInputField
                placeholder="Not Answered"
                value={mobileValue}
                onChange={(e) => {
                  const mobileValue = e.target.value.replace(/\D/g, "");
                  setMobileValue(mobileValue);
                  // Persist to localStorage
                  const saved = localStorage.getItem("add_member_form");
                  const form = saved ? JSON.parse(saved) : {};
                  localStorage.setItem(
                    "add_member_form",
                    JSON.stringify({
                      ...form,
                      mobile: e.target.value.replace(/\D/g, ""),
                    })
                  );
                }}
                type="tel"
                inputMode="numeric"
                size="md"
                showLabel={false}
                onFocus={() => setMobileFocused(true)}
                onBlur={() => setMobileFocused(false)}
              />
            </div>
          </>
        )}
        {selected === "email" && (
          <>
            <div>
              <MInputField
                label="What’s their email?"
                placeholder="Not Answered"
                value={emailValue}
                onChange={(e) => {
                  setEmailValue(e.target.value);
                  // Persist to localStorage
                  const saved = localStorage.getItem("add_member_form");
                  const form = saved ? JSON.parse(saved) : {};
                  localStorage.setItem(
                    "add_member_form",
                    JSON.stringify({ ...form, email: e.target.value })
                  );
                }}
                type="email"
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
              />
            </div>
          </>
        )}
      </div>
      <div className="confirm-footer">
        <MButton
          size="lg"
          variant="filled"
          className="w-full"
          disabled={
            (selected !== "email" && selected !== "mobile") ||
            (selected === "mobile" && !isValidMobile(mobileValue)) ||
            (selected === "email" && !isValidEmail(emailValue))
          }
          onClick={async () => {
            // check if mobile or email is selected and exist
            const exists = await checkIfMemberExists();
            if (exists) {
              return;
            }
            // Save contact info to local storage
            const saved = localStorage.getItem("add_member_form");
            const form = saved ? JSON.parse(saved) : {};
            let update: any = {};
            if (selected === "mobile") {
              update = {
                mobile: mobileValue,
                dialCode: dialCode,
              };
            } else if (selected === "email") {
              update = { email: emailValue };
            }
            localStorage.setItem(
              "add_member_form",
              JSON.stringify({ ...form, ...update })
            );
            // Navigate to consent
            navigate(
              `/profile/family/add-member/form/details/invite/contact/consent`,
              { state: { firstName, contactOption: selected } }
            );
          }}
        >
          Next
        </MButton>
      </div>
    </div>
  );
};

export default AddMemberContact;
